import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { FaLinkedin } from "react-icons/fa";
import { TbWorld } from "react-icons/tb";
import Select from "react-select";
import headerOne from "../assets/img/header_one.png";
import headerTwo from "../assets/img/header_two.png";
import headerThree from "../assets/img/header_three.png";
import "../assets/css/trial.css";
import { API_BASE_URL } from '../config';
import { motion, AnimatePresence } from "framer-motion";

const IMAGES = [headerOne, headerTwo, headerThree];

const validateEmailList = (emailsText) => {
    if (!emailsText) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = emailsText
        .split(/[,;]/)
        .map(email => email.trim())
        .filter(email => email !== "");
    return emails.length > 0 && emails.every(email => emailRegex.test(email));
};

const formatLatamContactPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 15);

    if (!digits) return "";
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;

    return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9)}`;
};

const normalizeLatamContactPhone = (value) => value.replace(/\D/g, "");

const TrialLatam = ({ encryptedData }) => {
    const [companyValid, setCompanyValid] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [navbarBg, setNavbarBg] = useState('bg-transparent');
    const [navbarColor, setNavbarColor] = useState('text-black');
    const [currentImage, setCurrentImage] = useState(0);
    const [fade, setFade] = useState(true);

    const totalGrupos = 2; // 1: Scope, 2: Additional Info
    const [grupoAtual, setGrupoAtual] = useState(1);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMessageOpen, setIsMessageOpen] = useState(false);
    const [message, setMessage] = useState("");

    const [countriesDb, setCountriesDb] = useState([]);
    const [jurisdictionsDb, setJurisdictionsDb] = useState([]);

    const [formData, setFormData] = useState({
        scope: [
            { id: Date.now(), country: null, jurisdictions: [] }
        ],
        missingJurisdictions: "",
        additionalInfo: {
            contactName: "",
            contactPhone: "",
            mcns: "",
            businessActivities: "",
            productsAndServices: "",
            crossBorderOperations: "",
            intercompanyTransactions: "",
            additionalRelevantInformation: "",
            distributionList: ""
        }
    });

    const navigate = useNavigate();

    useEffect(() => {
        const fetchInitialData = async () => {
            if (!encryptedData) {
                setCompanyValid(false);
                setIsLoading(false);
                return;
            }

            try {
                const companyReq = axios.post(`${API_BASE_URL}/validate-empresa-latam`, { encryptedData });
                const countriesReq = axios.get(`${API_BASE_URL}/search-countries-latam`);

                const [companyRes, countriesRes] = await Promise.allSettled([companyReq, countriesReq]);

                if (companyRes.status === 'fulfilled') {
                    setCompanyValid(!!companyRes.value.data.exists);
                } else {
                    setCompanyValid(false);
                }

                if (countriesRes.status === 'fulfilled') {
                    setCountriesDb(countriesRes.value.data.countries || []);
                }

            } catch (error) {
                console.error("Erro ao carregar dados iniciais:", error);
                setCompanyValid(false);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, [encryptedData]);

    useEffect(() => {
        const interval = setInterval(() => {
            setFade(false);
            setTimeout(() => {
                setCurrentImage((prev) => (prev + 1) % IMAGES.length);
                setFade(true);
            }, 250);
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const heroSection = document.getElementById('hero');
            if (heroSection) {
                if (window.scrollY > heroSection.offsetHeight) {
                    setNavbarBg('bg-white shadow');
                    setNavbarColor('#676a6c');
                } else {
                    setNavbarBg('bg-transparent');
                    setNavbarColor('white');
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleScrollToSection = useCallback((id) => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const handleChangeAdditionalInfo = (e) => {
        const { name, value } = e.target;
        const formattedValue = name === "contactPhone" ? formatLatamContactPhone(value) : value;

        setFormData(prev => ({
            ...prev,
            additionalInfo: { ...prev.additionalInfo, [name]: formattedValue }
        }));
    };

    const handleMissingJurisdictionsChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            missingJurisdictions: value
        }));
    };

    const proximoGrupo = useCallback(() => {
        if (grupoAtual < totalGrupos) setGrupoAtual(grupoAtual + 1);
    }, [grupoAtual]);

    const handleStepClick = useCallback((e) => {
        const group = e.target.dataset.group;
        if (group) {
            const newGrupo = parseInt(group.split('grupo')[1]);
            if (newGrupo && newGrupo >= 1 && newGrupo <= totalGrupos) {
                setGrupoAtual(newGrupo);
            }
        }
    }, [totalGrupos]);

    const addCountryBox = () => {
        setFormData(prev => ({
            ...prev,
            scope: [...prev.scope, { id: Date.now(), country: null, jurisdictions: [] }]
        }));
    };

    const removeCountryBox = (idToRemove) => {
        if (formData.scope.length === 1) return;
        setFormData(prev => ({
            ...prev,
            scope: prev.scope.filter(item => item.id !== idToRemove)
        }));
    };

    const handleCountryChange = async (scopeId, selectedOption) => {
        // Atualiza o estado limpando as jurisdições anteriores deste box
        setFormData(prev => ({
            ...prev,
            scope: prev.scope.map(item =>
                item.id === scopeId
                    ? { ...item, country: selectedOption, jurisdictions: [] }
                    : item
            )
        }));

        // Se um país foi selecionado, busca as jurisdições dinamicamente
        if (selectedOption && selectedOption.id) {
            const paisId = selectedOption.id;

            // Verifica se as jurisdições desse país já foram buscadas antes para evitar chamadas duplicadas
            const alreadyFetched = jurisdictionsDb.some(j => j.id_pais === paisId);

            if (!alreadyFetched) {
                try {
                    const res = await axios.get(`${API_BASE_URL}/search-jurisdictions-latam/${paisId}`);
                    const newJurisdictions = res.data.jurisdictions || [];

                    // Garante que o array retornado tem a referência do ID do país para o filtro funcionar
                    const mappedJurisdictions = newJurisdictions.map(j => ({ ...j, id_pais: paisId }));

                    setJurisdictionsDb(prev => [...prev, ...mappedJurisdictions]);
                } catch (error) {
                    console.error("Erro ao buscar jurisdições", error);
                }
            }
        }
    };

    const handleJurisdictionChange = (scopeId, selectedOptions) => {
        setFormData(prev => ({
            ...prev,
            scope: prev.scope.map(item =>
                item.id === scopeId ? { ...item, jurisdictions: selectedOptions || [] } : item
            )
        }));
    };

    const formatOptions = (data) => data.map((item) => ({
        label: item.nome,
        id: item.id,
        value: item.nome
    }));

    const optionsCountries = formatOptions(countriesDb);

    const validateForm = () => {
        const { additionalInfo, scope } = formData;

        if (scope.length === 0) return "Please add at least one country in the scope.";
        for (let i = 0; i < scope.length; i++) {
            if (!scope[i].country) return `Please select a country.`;
            if (!scope[i].jurisdictions || scope[i].jurisdictions.length === 0) {
                return `Please select at least one Subnational Jurisdiction for ${scope[i].country.label}.`;
            }
        }

        if (!additionalInfo.businessActivities.trim()) return "Business Activities is required.";
        if (!additionalInfo.productsAndServices.trim()) return "Products and Services is required.";
        if (!additionalInfo.crossBorderOperations.trim()) return "Cross-Border Operations is required.";
        if (!additionalInfo.intercompanyTransactions.trim()) return "Intercompany Transactions is required.";
        if (!additionalInfo.additionalRelevantInformation.trim()) return "Additional Relevant Information is required.";

        // Distribuição de e-mails
        if (!additionalInfo.distributionList.trim()) return "Distribution list (emails) is required.";
        if (!validateEmailList(additionalInfo.distributionList)) return "Invalid email addresses found in the distribution list.";

        return null;
    };

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        const errorMessage = validateForm();
        if (errorMessage) {
            setMessage(errorMessage + " ❌");
            setIsMessageOpen(true);
            return;
        }
        setIsModalOpen(true);
    }, [formData]);

    const handleConfirm = useCallback(async () => {
        setIsModalOpen(false);
        setIsLoading(true);

        try {
            const contactPhone = normalizeLatamContactPhone(formData.additionalInfo.contactPhone);
            const payload = {
                encryptedData,
                scope: formData.scope,
                missingJurisdictions: formData.missingJurisdictions,
                additionalInfo: {
                    ...formData.additionalInfo,
                    contactPhone
                },
                flg_setup_latam: 0,
            };

            await axios.post(`${API_BASE_URL}/save-empresa-latam`, payload);

            setIsLoading(false);
            setMessage("Your request has been successfully submitted! We will contact you soon. ✅");
            setIsMessageOpen(true);

            setTimeout(() => {
                navigate("/");
            }, 3000);
        } catch (error) {
            setIsLoading(false);
            const errorMsg = error.response?.data?.error || error.response?.data?.message || "An error occurred! Please contact our support.";
            setMessage(`${errorMsg} ❌`);
            setIsMessageOpen(true);
        }
    }, [formData, encryptedData, navigate]);

    if (isLoading) return <div className="loader-wrapper"><div className="loader"></div></div>;

    // Bloqueia a tela caso o trial latam não seja autorizado ou já tenha sido feito
    if (companyValid === false) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', backgroundColor: 'white' }}>
            <div className="text-center text-dark display-4">This form is locked or already submitted.</div>
        </div>
    );

    const renderScope = () => (
        <AnimatePresence>
            <motion.div className="formulario-grupo w-100" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <h3 className="grupo-titulo mb-4">Scope</h3>
                <p className="text-muted mb-4">Please provide the following information to tailor the Report to your organization:</p>

                {formData.scope.map((item) => {
                    const countryJurisdictions = jurisdictionsDb.filter(j => j.id_pais === item.country?.id);
                    const optionsJurisdictions = formatOptions(countryJurisdictions);

                    return (
                        <div key={item.id} className="p-4 border rounded bg-light mb-4 position-relative">
                            {formData.scope.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger position-absolute"
                                    style={{ top: '10px', right: '10px' }}
                                    onClick={() => removeCountryBox(item.id)}
                                >
                                    Remove
                                </button>
                            )}

                            <div className="passo-item mb-3">
                                <label className="font-weight-bold">Countries in Scope <span className="text-danger">*</span></label>
                                <small className="text-muted d-block mb-1">List of the countries you would like to include in the Report.</small>
                                <Select
                                    value={item.country}
                                    options={optionsCountries}
                                    className="basic-single-select"
                                    classNamePrefix="select"
                                    onChange={(v) => handleCountryChange(item.id, v)}
                                    placeholder="Select a country..."
                                    noOptionsMessage={() => "No results found"}
                                />
                            </div>

                            <div className="passo-item mb-2">
                                <label className="font-weight-bold">Subnational Jurisdictions <span className="text-danger">*</span></label>
                                <small className="text-muted d-block mb-1">For each selected country, please specify the relevant jurisdictions (e.g., provinces, states, cantons, municipalities).</small>
                                <Select
                                    value={item.jurisdictions}
                                    isMulti
                                    options={optionsJurisdictions}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    onChange={(v) => handleJurisdictionChange(item.id, v)}
                                    placeholder="Select jurisdictions..."
                                    noOptionsMessage={() => "Select a country first or no results"}
                                    isDisabled={!item.country}
                                />
                            </div>
                        </div>
                    );
                })}

                <div className="text-center mt-3">
                    <button type="button" className="btn btn-outline-primary rounded-pill px-4 fw-bold" onClick={addCountryBox}>
                        + ADD COUNTRY
                    </button>
                </div>

                <div className="mt-4">
                    <label className="font-weight-bold">If any required local jurisdiction cannot be selected from the list above, please specify it/them here.</label>
                    <textarea
                        name="missingJurisdictions"
                        value={formData.missingJurisdictions}
                        onChange={handleMissingJurisdictionsChange}
                        className="text_area form-control"
                        placeholder="Please list any required local jurisdiction that is not available in the selection above."
                    />
                </div>
            </motion.div>
        </AnimatePresence>
    );

    const renderAdditionalInfo = () => (
        <AnimatePresence>
            <motion.div className="formulario-grupo w-100" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <h3 className="grupo-titulo mb-4">Additional Information</h3>

                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="font-weight-bold">Contact Name</label>
                        <input
                            type="text"
                            name="contactName"
                            value={formData.additionalInfo.contactName}
                            onChange={handleChangeAdditionalInfo}
                            className="input form-control"
                            placeholder="Name of the designated communication focal point."
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="font-weight-bold">Contact Phone Number</label>
                        <input
                            type="text"
                            name="contactPhone"
                            value={formData.additionalInfo.contactPhone}
                            onChange={handleChangeAdditionalInfo}
                            className="input form-control"
                            placeholder="55 28 99918-2544"
                        />
                        <small className="text-muted d-block mt-1">Please include the country code.</small>
                    </div>
                </div>

                <div className="mb-3">
                    <label className="font-weight-bold">MCNs</label>
                    <textarea
                        name="mcns"
                        value={formData.additionalInfo.mcns}
                        onChange={handleChangeAdditionalInfo}
                        className="text_area form-control"
                        placeholder="Kindly provide the MCNs (i.e., tariff positions) to be incorporated into the scope of the 24/7 monitoring for the 15-day tailored TaxUpdate LATAM trial reports."
                    />
                </div>

                <div className="mb-3">
                    <label className="font-weight-bold">Business Activities <span className="text-danger">*</span></label>
                    <textarea
                        name="businessActivities"
                        value={formData.additionalInfo.businessActivities}
                        onChange={handleChangeAdditionalInfo}
                        className="text_area form-control"
                        placeholder="Please describe the main activities carried out by your company in each jurisdiction, as well as any secondary or ancillary activities that may be relevant."
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="font-weight-bold">Products and Services <span className="text-danger">*</span></label>
                    <textarea
                        name="productsAndServices"
                        value={formData.additionalInfo.productsAndServices}
                        onChange={handleChangeAdditionalInfo}
                        className="text_area form-control"
                        placeholder="Describe the main products and/or services manufactured, distributed, or marketed in each jurisdiction."
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="font-weight-bold">Cross-Border Operations <span className="text-danger">*</span></label>
                    <textarea
                        name="crossBorderOperations"
                        value={formData.additionalInfo.crossBorderOperations}
                        onChange={handleChangeAdditionalInfo}
                        className="text_area form-control"
                        placeholder="Does your company engage in import and/or export activities? If so, please specify the main goods and/or services involved."
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="font-weight-bold">Intercompany Transactions <span className="text-danger">*</span></label>
                    <textarea
                        name="intercompanyTransactions"
                        value={formData.additionalInfo.intercompanyTransactions}
                        onChange={handleChangeAdditionalInfo}
                        className="text_area form-control"
                        placeholder="Does your company engage in intercompany transactions? If yes, please briefly describe their nature (e.g., services, financing, licensing, goods)."
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="font-weight-bold">Additional Relevant Information <span className="text-danger">*</span></label>
                    <textarea
                        name="additionalRelevantInformation"
                        value={formData.additionalInfo.additionalRelevantInformation}
                        onChange={handleChangeAdditionalInfo}
                        className="text_area form-control"
                        placeholder="Please include any other information that may be relevant for tax analysis. Note: The more detailed the information provided, the more tailored and accurate the Report will be."
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="font-weight-bold">Distribution List <span className="text-danger">*</span></label>
                    <textarea
                        name="distributionList"
                        value={formData.additionalInfo.distributionList}
                        onChange={handleChangeAdditionalInfo}
                        className="text_area form-control"
                        placeholder="Please provide the email addresses of the intended recipients of the Report, separated by commas."
                        required
                    />
                </div>
            </motion.div>
        </AnimatePresence>
    );

    return (
        <div style={{ backgroundColor: 'white' }}>
            <nav className={`navbar navbar-expand-lg py-2 ${navbarBg} fixed-top w-100 z-3`}>
                <div className="container">
                    <a className="px-2 py-1 text-white bg-custom" href="https://cliente.taxupdate.com.br/" target="_blank" rel="noreferrer">
                        CLIENT AREA
                    </a>
                    <div className="collapse navbar-collapse justify-content-end">
                        <ul className="navbar-nav ms-auto">
                            {['HOME', 'STEPS'].map((item, index) => (
                                <li key={index} className="nav-item">
                                    <a className="nav-link active fw-bold" style={{ color: navbarColor, cursor: 'pointer' }} onClick={() => handleScrollToSection(index === 0 ? 'topo' : 'steps')}>
                                        {item}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </nav>

            <div className={`text-start py-5 bg-cover bg-center position-relative transition-opacity ${fade ? 'opacity-100' : 'opacity-0'}`} style={{ backgroundImage: `url(${IMAGES[currentImage]})`, minHeight: '500px' }} id="topo">
                <div className="container pt-5 mt-4">
                    {currentImage === 0 && <><h1 className="display-4 text-white fs-custom-title" id="hero">TAILOR YOUR SCOPE<br />FOR PRECISE MONITORING.</h1></>}
                    {currentImage === 1 && <><h1 className="display-4 text-white fs-custom-title" id="hero">CUSTOMIZE YOUR MONITORING<br />IN MINUTES.</h1></>}
                    {currentImage === 2 && <><h1 className="display-4 text-white fs-custom-title" id="hero">PERSONALIZE YOUR<br />TAX MONITORING.</h1></>}
                </div>
            </div>

            <AnimatePresence>
                <motion.div className="container pt-5 mt-5" id="steps">
                    <div className="row text-center justify-content-center">
                        {['Scope', 'Additional Info'].map((passo, index) => {
                            const isActive = grupoAtual === (index + 1);
                            return (
                                <div key={index} className="col-md-5">
                                    <h4 style={{ color: isActive ? '#007bff' : '#6C737F', fontSize: '1.2rem', fontWeight: isActive ? 'bold' : 'normal', textDecoration: isActive ? 'underline' : 'none', cursor: 'pointer' }} data-group={`grupo${index + 1}`} onClick={handleStepClick}>
                                        {passo}
                                    </h4>
                                    <p style={{ color: '#aeaeae' }}>Step {index + 1} of {totalGrupos}</p>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>
            </AnimatePresence>

            <AnimatePresence>
                <motion.div className="container pt-3 mt-3 formulario">
                    <form onSubmit={handleSubmit} className="mx-auto" style={{ maxWidth: "800px" }}>
                        <div className="d-flex justify-content-center align-items-center w-100">
                            {grupoAtual === 1 && renderScope()}
                            {grupoAtual === 2 && renderAdditionalInfo()}
                        </div>
                    </form>

                    <div className="d-flex w-100 justify-content-between align-items-center mb-4 mt-4" style={{ maxWidth: "800px", margin: "0 auto" }}>
                        {grupoAtual === 1 ? <p></p> : <button type="button" className="btn btn-secondary rounded-pill px-4" onClick={() => setGrupoAtual(grupoAtual - 1)}>❮ Back</button>}
                        {grupoAtual < totalGrupos ? (
                            <button type="button" className="btn btn-success rounded-pill px-4" onClick={proximoGrupo}>Next ❯</button>
                        ) : (
                            <button type="button" className="btn btn-primary rounded-pill px-4" onClick={handleSubmit}>Complete ✔</button>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>

            <AnimatePresence>
                {isModalOpen && (
                    <motion.div className="modal-overlay">
                        <motion.div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                            <h2 className="mb-4">Confirm your data</h2>

                            <div className="modal-body text-start">
                                <div className="modal-section mb-4">
                                    <h4 className="border-bottom pb-2">Scope Selected</h4>
                                    {formData.scope.map((s, i) => (
                                        <div key={i} className="mb-3">
                                            <label className="font-weight-bold">Country:</label><br />
                                            <span>{s.country?.label || 'None'}</span><br />
                                            <label className="font-weight-bold mt-1">Jurisdictions:</label><br />
                                            <span>{s.jurisdictions.length ? s.jurisdictions.map(j => j.label).join(", ") : 'None'}</span>
                                        </div>
                                    ))}
                                    <label className="font-weight-bold mt-2">Missing Local Jurisdictions:</label>
                                    <p className="mb-0 text-muted">{formData.missingJurisdictions || 'None'}</p>
                                </div>

                                <div className="modal-section mb-4">
                                    <h4 className="border-bottom pb-2">Additional Information</h4>

                                    <label className="font-weight-bold mt-2">Contact Name:</label>
                                    <p className="mb-2 text-muted">{formData.additionalInfo.contactName || 'None'}</p>

                                    <label className="font-weight-bold mt-2">Contact Phone Number:</label>
                                    <p className="mb-2 text-muted">{formData.additionalInfo.contactPhone || 'None'}</p>

                                    <label className="font-weight-bold mt-2">MCNs:</label>
                                    <p className="mb-2 text-muted">{formData.additionalInfo.mcns || 'None'}</p>

                                    <label className="font-weight-bold mt-2">Business Activities:</label>
                                    <p className="mb-2 text-muted">{formData.additionalInfo.businessActivities}</p>

                                    <label className="font-weight-bold mt-2">Products and Services:</label>
                                    <p className="mb-2 text-muted">{formData.additionalInfo.productsAndServices}</p>

                                    <label className="font-weight-bold mt-2">Cross-Border Operations:</label>
                                    <p className="mb-2 text-muted">{formData.additionalInfo.crossBorderOperations}</p>

                                    <label className="font-weight-bold mt-2">Intercompany Transactions:</label>
                                    <p className="mb-2 text-muted">{formData.additionalInfo.intercompanyTransactions}</p>

                                    <label className="font-weight-bold mt-2">Additional Relevant Information:</label>
                                    <p className="mb-2 text-muted">{formData.additionalInfo.additionalRelevantInformation}</p>

                                    <label className="font-weight-bold mt-2">Distribution List:</label>
                                    <p className="mb-0 text-muted">{formData.additionalInfo.distributionList}</p>
                                </div>
                            </div>

                            <div className="modal-actions d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                                <button className="btn btn-secondary px-4" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                <button className="btn btn-primary px-4" onClick={handleConfirm}>Confirm</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isMessageOpen && (
                    <motion.div className="modal-overlay">
                        <motion.div className="modal-content-message">
                            <h2 style={{ whiteSpace: "pre-wrap" }}>{message}</h2>
                            <button onClick={() => setIsMessageOpen(false)} className="btn btn-primary mt-3">OK</button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="pt-5 mt-5">
                <footer style={{ backgroundColor: "#ECF5F3" }} className="py-4">
                    <div className="container">
                        <div className="row justify-content-center text-center">
                            <div className="col-12">
                                <div className="mb-4 d-flex justify-content-center" style={{ gap: "5px" }}>
                                    <a href="https://www.linkedin.com/company/taxupdate/" target="_blank" rel="noreferrer">
                                        <button type="button" className="btn btn-secondary rounded-circle d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}><FaLinkedin /></button>
                                    </a>
                                    <a href="https://taxupdate.com.br/" target="_blank" rel="noreferrer">
                                        <button type="button" className="btn btn-secondary rounded-circle d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px", fontSize: "30px" }}><TbWorld /></button>
                                    </a>
                                </div>
                                <p style={{ color: "#333", fontWeight: "bold" }}>© {new Date().getFullYear()} TaxUpdate</p>
                                <p className="mb-0" style={{ color: "#333" }}>All rights reserved</p>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default TrialLatam;